/**
 * Bash's phone, the web's half of /api/phone/* (spec 2026-10-14 §6.2). Every
 * route answers 404 while the server's BASH_PHONE_V1 is off; getPhoneStatus
 * turns that 404 into null, read from the status field, so the page, the
 * onboarding beat and the first-open ask hide themselves instead of erroring.
 * Its 503 (their numbers could not be read) throws: no reader asks for a number.
 */
import type {
  LiveVoice,
  PhoneCallsResponse,
  PhonePinStatusResponse,
  PhoneVerifyError,
  PhoneVerifyStartResponse,
  PhoneVerifyStatusResponse,
  PhoneVoiceResponse,
} from "@bash/shared";
import { ApiError, authedFetch } from "./client";

export async function getPhoneStatus(): Promise<PhoneVerifyStatusResponse | null> {
  try {
    const res = await authedFetch("/api/phone/verify/status");
    return (await res.json()) as PhoneVerifyStatusResponse;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export type StartVerificationResult =
  | { ok: true; body: PhoneVerifyStartResponse }
  | { ok: false; error: PhoneVerifyError | "unknown" };

/** POST /api/phone/verify/start. A refusal carries the server's own code. */
export async function startVerification(
  e164: string
): Promise<StartVerificationResult> {
  try {
    const res = await authedFetch("/api/phone/verify/start", {
      method: "POST",
      body: JSON.stringify({ e164 }),
    });
    return { ok: true, body: (await res.json()) as PhoneVerifyStartResponse };
  } catch (err) {
    const code = err instanceof ApiError ? err.code : null;
    return { ok: false, error: (code as PhoneVerifyError | null) ?? "unknown" };
  }
}

export async function removeNumber(id: string): Promise<void> {
  await authedFetch(`/api/phone/numbers/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function getPin(): Promise<PhonePinStatusResponse> {
  const res = await authedFetch("/api/phone/pin");
  return (await res.json()) as PhonePinStatusResponse;
}

/** PUT /api/phone/pin. 204 is "ok", 400 bad_pin is "bad_pin"; anything else throws. */
export async function setPin(pin: string): Promise<"ok" | "bad_pin"> {
  try {
    await authedFetch("/api/phone/pin", {
      method: "PUT",
      body: JSON.stringify({ pin }),
    });
    return "ok";
  } catch (err) {
    if (err instanceof ApiError && err.code === "bad_pin") return "bad_pin";
    throw err;
  }
}

export async function unlockPin(): Promise<void> {
  await authedFetch("/api/phone/pin/unlock", { method: "POST" });
}

export async function getVoice(): Promise<PhoneVoiceResponse> {
  const res = await authedFetch("/api/phone/voice");
  return (await res.json()) as PhoneVoiceResponse;
}

/** PUT /api/phone/voice. `onlyIfUnset` is the one-time copy of the browser's old choice: it never overwrites one. */
export async function setVoice(
  voice: LiveVoice | null,
  onlyIfUnset = false
): Promise<void> {
  await authedFetch("/api/phone/voice", {
    method: "PUT",
    body: JSON.stringify(onlyIfUnset ? { voice, onlyIfUnset } : { voice }),
  });
}

export async function getCalls(): Promise<PhoneCallsResponse> {
  const res = await authedFetch("/api/phone/calls");
  return (await res.json()) as PhoneCallsResponse;
}
